import axios, { InternalAxiosRequestConfig, AxiosError } from "axios";
import { routeUrls } from "../config/routeUrls";
import { tokenManager } from "./refreshTokenService/refreshTokenService";
import { IsAuthorized, GetRefreshToken } from "../utils/helpers/auth";

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    
    failedQueue = [];
};

// Configurazione dell'interceptor globale per axios
const setupAxiosInterceptor = () => {
    // Request interceptor per aggiungere automaticamente l'Authorization header
    axios.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            const token = window.sessionStorage.getItem('accessToken');
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor per gestire gli errori 401 con refresh automatico
    axios.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error: AxiosError) => {
            const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
            
            if (error.response && error.response.status === 401 && !originalRequest._retry) {
                // Se non abbiamo un refresh token o non siamo autorizzati, vai al logout
                if (!GetRefreshToken() || !IsAuthorized()) {
                    console.log('No refresh token available, redirecting to logout');
                    window.location.href = routeUrls.authorize;
                    return Promise.reject(error);
                }

                // Evita loop infiniti
                originalRequest._retry = true;

                if (isRefreshing) {
                    // Se c'è già un refresh in corso, accoda questa richiesta
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(() => {
                        // Riprova la richiesta originale con il nuovo token
                        const newToken = window.sessionStorage.getItem('accessToken');
                        if (originalRequest.headers && newToken) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        }
                        return axios(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                isRefreshing = true;

                try {
                    console.log('401 error detected, attempting token refresh...');
                    const refreshSuccess = await tokenManager.refreshTokenSilently();
                    
                    if (refreshSuccess) {
                        console.log('Token refresh successful, retrying original request');
                        const newToken = window.sessionStorage.getItem('accessToken');
                        processQueue(null, newToken);
                        
                        // Riprova la richiesta originale con il nuovo token
                        if (originalRequest.headers && newToken) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        }
                        
                        isRefreshing = false;
                        return axios(originalRequest);
                    } else {
                        console.log('Token refresh failed, redirecting to login');
                        processQueue(error, null);
                        isRefreshing = false;
                        window.location.href = routeUrls.authorize;
                        return Promise.reject(error);
                    }
                } catch (refreshError) {
                    console.error('Error during token refresh:', refreshError);
                    processQueue(refreshError, null);
                    isRefreshing = false;
                    window.location.href = routeUrls.authorize;
                    return Promise.reject(error);
                }
            }
            
            // Per altri errori, propaga l'errore normalmente
            return Promise.reject(error);
        }
    );
};

export default setupAxiosInterceptor; 